/* eslint-disable max-lines */
/* tslint:disable:max-file-line-count */

import { comparer, isArrayLike } from 'mobx';

import { ILocalization } from 'bernie-l10n';
import { merge, SerializedData } from 'bernie-core';

import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';

import { PackageType } from 'models/multi-item/multi-item-types';

import { multiItemCarSearchCriteria, multiItemFlightSearchCriteria } from 'components/multi-item/utils';

import { PropertySort, SearchCriteriaModel } from './search-criteria-model';
import { RoomModel } from './room-model';

export interface UrlParamQueryIdMapper {
  URL_PARAM_ID: string;
  QUERY_BOOLEAN_ID: string;
}

export const URL_PARAM_TO_QUERY_ID_MAPPER = [
  {
    URL_PARAM_ID: 'packageRates',
    QUERY_BOOLEAN_ID: 'showPackageRates',
  },
];

export const untrackedQueryParams = [
  '0',
  '1',
  '2',
  'authSuccessMsg',
  'hotelId',
  'lang',
  'prefix',
  'pwaDialog',
  'pwaDialogNested',
  'pwaOverlay',
  'scrollPosition',
  'splat',
  'trv_curr',
  'trv_dp',
  'exp_dp',
  'exp_tx',
  'trv_bp',
];
export const singleUseQueryParams = ['rfrr', 'top_dp', 'top_cur', 'trv_dp', 'trv_curr', 'authSuccessMsg', 'exp_dp', 'exp_tx', 'trv_bp'];
export const getSearchImpactingKeys = () => [
  'startDate',
  'endDate',
  'regionId',
  'selected',
  'latLong',
  'destination',
  'rooms',
  'sort',
  'hotelName',
  'price',
  'star',
  'lodging',
  'amenities',
  'travelerType',
  'poi',
  'useRewards', // Exp 29018 PWA_SwP_Toggle_with_more_than_1_in_points
  'guestRating',
  'accessibility',
  'paymentType',
  'cleaningAndSafetyPractices',
  'bedroomFilter',
  'deals', // Deals Filter
  'propertyStyle',
  'misId',
  'rewards',
  'commissionTier',
  'agencyBusinessModel',
  'mealPlan',
  'neighborhood',
  'stayType',
];

export const getMultiItemMutationImpactingKeys = (packageType: PackageType) => {
  const keys: string[] = ['startDate', 'endDate', 'destination', 'rooms'];

  if (packageType && packageType.includes('F')) {
    keys.push(...multiItemFlightSearchCriteria.filter(key => key !== 'airlineCode'));
  }

  if (packageType && packageType === PackageType.HC) {
    keys.push(...multiItemCarSearchCriteria);
  }

  return keys;
};

const getToday = (): string => new Date().toISOString().split('T')[0];

const getTomorrow = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return tomorrow.toISOString().split('T')[0];
};

const parseInfositeValues = (query: any, l10n: ILocalization, localDateFormat: string): SerializedData => {
  const iqaParsedInQuery = InfositeQueryAdapter.parseIn(query);
  const { adults, children, endDate, startDate } = SearchQueryAdapter.parseOut(iqaParsedInQuery);

  return {
    ...iqaParsedInQuery,
    adults,
    children,
    endDate: sanitizeDateToISO(endDate, l10n, localDateFormat),
    rooms: setDefaultRoomsIfNecessary(iqaParsedInQuery.rooms),
    startDate: sanitizeDateToISO(startDate, l10n, localDateFormat),
  };
};

const sanitizeDateToISO = (date: string, l10n: ILocalization, localDateFormat: string): string => {
  if (date) {
    let dateObj: Date = l10n.parseLocalDateString(date, { raw: localDateFormat }) as Date;

    if (!dateObj) {
      dateObj = l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
    }

    const isoDateString = l10n.formatDate(dateObj, { raw: 'yyyy-MM-dd' });

    if (isoDateString) {
      return isoDateString;
    }
  }

  return '';
};

const localizeDateFromISO = (date: string, l10n: ILocalization, localDateFormat: string): string => {
  if (date) {
    const dateObj: Date = l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
    const localDate = l10n.formatDate(dateObj, { raw: localDateFormat });

    if (localDate) {
      return localDate;
    }
  }

  return '';
};

const setDefaultRoomsIfNecessary = (rooms?: RoomModel[]) => {
  if (!rooms || !isArrayLike(rooms) || rooms.length === 0) {
    return [{ adults: 2, children: [] }];
  }

  return rooms;
};

interface CriteriaTypePair {
  key: string;
  type: string;
}

function getFilterKeys(): CriteriaTypePair[] {
  return [
    {
      key: 'sort',
      type: 'string',
    },
    {
      key: 'hotelName',
      type: 'string',
    },
    {
      key: 'price',
      type: 'array',
    },
    {
      key: 'star',
      type: 'array',
    },
    {
      key: 'lodging',
      type: 'array',
    },
    {
      key: 'amenities',
      type: 'array',
    },
    {
      key: 'travelerType',
      type: 'array',
    },
    {
      key: 'poi',
      type: 'string',
    },
    {
      key: 'guestRating',
      type: 'string',
    },
    {
      key: 'accessibility',
      type: 'array',
    },
    {
      key: 'paymentType',
      type: 'array',
    },
    {
      key: 'cleaningAndSafetyPractices',
      type: 'array',
    },
    {
      key: 'bedroomFilter',
      type: 'array',
    },
    {
      key: 'deals',
      type: 'array',
    },
    {
      key: 'propertyStyle',
      type: 'array',
    },
    {
      key: 'rewards',
      type: 'array',
    },
    {
      key: 'commissionTier',
      type: 'array',
    },
    {
      key: 'agencyBusinessModel',
      type: 'array',
    },
    {
      key: 'mealPlan',
      type: 'array',
    },
    {
      key: 'paandi',
      type: 'string',
    },
    {
      key: 'neighborhood',
      type: 'string',
    },
    {
      key: 'stayType',
      type: 'array',
    },
  ];
}

function getFilterDefaults(): SerializedData {
  return {
    accessibility: [],
    amenities: [],
    guestRating: '',
    hotelName: '',
    lodging: [],
    poi: '',
    price: [],
    sort: PropertySort.RECOMMENDED,
    star: [],
    travelerType: [],
    paymentType: [],
    cleaningAndSafetyPractices: [],
    bedroomFilter: [],
    deals: [],
    propertyStyle: [],
    rewards: [],
    commissionTier: [],
    agencyBusinessModel: [],
    mealPlan: [],
    directFlights: 'false',
    neighborhood: '',
    stayType: [],
  };
}

function ensureCorrectTypes(query: SerializedData): SerializedData {
  const correctedQuery = { ...query };
  const filterDefaults = getFilterDefaults();
  // iterate through the filters setting them to a parsed value or their default
  getFilterKeys().forEach(({ key, type }) => {
    if (key in correctedQuery) {
      // if it's a string but it's supposed to be an array
      if (type === 'array' && typeof correctedQuery[key] === 'string') {
        // parse based on comma separator
        correctedQuery[key] = correctedQuery[key].split(',').filter(val => val !== '');
      }
      // if it's a array but it's supposed to be an string
      if (type === 'string' && Array.isArray(correctedQuery[key])) {
        correctedQuery[key] = correctedQuery[key].join();
      }
    } else {
      correctedQuery[key] = filterDefaults[key];
    }
  });

  return correctedQuery;
}

function hasSearchCriteriaChanged(prevSearchCriteria: SearchCriteriaModel, newSearchCriteria: SearchCriteriaModel, compareKeys: string[]) {
  const prevSearchParams = merge.withKeys(compareKeys).merge({}, prevSearchCriteria.toJSON());
  const newSearchParams = merge.withKeys(compareKeys).merge({}, newSearchCriteria.toJSON());

  return !comparer.structural(prevSearchParams, newSearchParams);
}
export const SearchCriteriaUtils = {
  getFilterDefaults,
  ensureCorrectTypes,
  getToday,
  getTomorrow,
  localizeDateFromISO,
  parseInfositeValues,
  sanitizeDateToISO,
  hasSearchCriteriaChanged,
};
